import { Rng, hashSeed } from "@enjoyment/core";
import type { TimelineEvent } from "./events";
import type { ZamanLevel } from "./generator";

export type GamePhase = "placing" | "revealing" | "won" | "lost";

export interface MoveResult {
  id: string;
  correct: boolean;
  tappedSlot: number;
  correctSlot: number;
  actualSlot: number;
  year: number;
}

export interface ZamanState {
  level: number;
  definition: ZamanLevel;
  phase: GamePhase;
  moves: number;
  mistakes: number;
  cardsTotal: number;
  timeline: TimelineEvent[];
  current: TimelineEvent | null;
  /** Deterministic, shuffled cards still to place (anchor is already in timeline). */
  deck: TimelineEvent[];
  /** First accepted insertion slot; equal-year runs may have more than one valid slot. */
  correctSlot: number;
  stars: 0 | 1 | 2 | 3;
  history: MoveResult[];
  lastMove: MoveResult | null;
}

export function acceptedSlots(state: Pick<ZamanState, "timeline" | "current">): number[] {
  if (!state.current) return [];
  const years = state.timeline.map((event) => event.year);
  let first = 0;
  while (first < years.length && years[first]! < state.current.year) first++;
  let after = first;
  while (after < years.length && years[after] === state.current.year) after++;
  return Array.from({ length: after - first + 1 }, (_, index) => first + index);
}

function firstCorrectSlot(timeline: readonly TimelineEvent[], current: TimelineEvent | null): number {
  if (!current) return 0;
  let slot = 0;
  while (slot < timeline.length && timeline[slot]!.year < current.year) slot++;
  return slot;
}

export function createGame(level: ZamanLevel): ZamanState {
  if (level.events.length !== level.cardsToPlace + 1) throw new Error("A Zaman level needs one anchor plus its placement cards");
  if (new Set(level.events.map((event) => event.year)).size !== level.events.length) throw new Error("A Zaman level must use distinct years");
  const anchorRng = new Rng(hashSeed(`zaman:anchor:${level.seed}`));
  const anchorIndex = anchorRng.int(level.events.length);
  const anchor = level.events[anchorIndex]!;
  const deck = level.events.filter((_, index) => index !== anchorIndex);
  const shuffle = new Rng(hashSeed(`zaman:deck:${level.seed}`));
  for (let i = deck.length - 1; i > 0; i--) {
    const j = shuffle.int(i + 1);
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  const current = deck[0] ?? null;
  const timeline = [anchor];
  return {
    level: level.n,
    definition: level,
    phase: "placing",
    moves: 0,
    mistakes: 0,
    cardsTotal: level.cardsToPlace,
    timeline,
    current,
    deck,
    correctSlot: firstCorrectSlot(timeline, current),
    stars: 0,
    history: [],
    lastMove: null,
  };
}

/** Tap a gap slot. The returned state enters `revealing`; call finishReveal after the UI animation. */
export function placeCard(state: ZamanState, slotIndex: number): ZamanState {
  if (state.phase !== "placing" || !state.current || !Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex > state.timeline.length) return state;
  const valid = acceptedSlots(state);
  const correct = valid.includes(slotIndex);
  const at = firstCorrectSlot(state.timeline, state.current);
  const timeline = [...state.timeline, state.current].sort((a, b) => a.year - b.year || a.id.localeCompare(b.id));
  const actualSlot = timeline.findIndex((event) => event.id === state.current!.id);
  const result: MoveResult = { id: state.current.id, correct, tappedSlot: slotIndex, correctSlot: at, actualSlot, year: state.current.year };
  const moves = state.moves + 1;
  const mistakes = state.mistakes + (correct ? 0 : 1);
  return {
    ...state,
    phase: "revealing",
    moves,
    mistakes,
    timeline,
    correctSlot: at,
    history: [...state.history, result],
    lastMove: result,
  };
}

/** Complete the card-flip animation and either show the next card or resolve the level. */
export function finishReveal(state: ZamanState): ZamanState {
  if (state.phase !== "revealing") return state;
  if (state.moves >= state.cardsTotal) {
    const won = state.mistakes <= Math.floor(state.cardsTotal / 2);
    const stars: 0 | 1 | 2 | 3 = state.mistakes === 0 ? 3 : state.mistakes <= 2 ? 2 : 1;
    return { ...state, phase: won ? "won" : "lost", current: null, correctSlot: 0, stars: won ? stars : 0 };
  }
  const current = state.deck[state.moves] ?? null;
  const next = { ...state, phase: "placing" as const, current };
  return { ...next, correctSlot: firstCorrectSlot(next.timeline, current) };
}

/** Replay slot indices in turn order. An invalid slot halts replay without changing state. */
export function replay(level: ZamanLevel, slots: readonly number[]): ZamanState {
  let state = createGame(level);
  for (const slot of slots) {
    if (state.phase !== "placing") break;
    const next = placeCard(state, slot);
    if (next === state) break;
    state = finishReveal(next);
  }
  return state;
}
